import { useEffect, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { TrendingUp, Trophy, Users, Goal, Footprints } from "lucide-react";

function ProgressBar({ value, color }) {
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.min(100, value)}%`, background: color }}
      />
    </div>
  );
}

function ScorerTable({ rows, metric }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="p-10 text-center text-slate-400 text-sm">
        Inserisci il risultato di una partita per vedere la classifica.
      </div>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r[metric] || 0));
  return (
    <div className="divide-y divide-slate-100">
      {rows.slice(0, 15).map((r, i) => (
        <div key={r.player_id} className="px-5 py-3 flex items-center gap-4">
          <span
            className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
              i === 0
                ? "bg-amber-100 text-amber-700"
                : i === 1
                ? "bg-slate-200 text-slate-700"
                : i === 2
                ? "bg-orange-100 text-orange-700"
                : "bg-slate-50 text-slate-500"
            }`}
          >
            {i + 1}
          </span>
          <span className="h-7 min-w-7 px-1.5 rounded bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">
            {r.jersey_number ?? "—"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">
              {r.last_name} {r.first_name}
            </div>
            <ProgressBar
              value={((r[metric] || 0) / max) * 100}
              color={metric === "goals" ? "#22c55e" : "#0ea5e9"}
            />
          </div>
          <span className="font-heading font-bold text-2xl tabular-nums">
            {r[metric] || 0}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function StatsPage() {
  const { activeTeam, season } = useTeams();
  const [data, setData] = useState(null);
  const [scorers, setScorers] = useState({ scorers: [], assistmen: [] });

  useEffect(() => {
    if (!activeTeam) return;
    (async () => {
      const [s, sc] = await Promise.all([
        api.get(`/teams/${activeTeam.id}/stats`, { params: { season } }),
        api.get(`/teams/${activeTeam.id}/scorers`),
      ]);
      setData(s.data);
      setScorers(sc.data);
    })();
  }, [activeTeam, season]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;
  if (!data) return <p className="text-slate-400">Caricamento...</p>;

  const { totals, players } = data;
  const goalDiff = (totals.goals_scored || 0) - (totals.goals_conceded || 0);

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div>
        <div className="label-eyebrow">Statistiche</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Prestazioni squadra</h1>
        <p className="text-slate-500">
          Aggregate da {totals.trainings} allenamenti, {totals.matches} sessioni di presenza,{" "}
          {totals.matches_played || 0} partite giocate.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Giocatori", value: totals.players, icon: Users, color: "#0ea5e9" },
          { label: "Allenamenti", value: totals.trainings, icon: TrendingUp, color: "#8b5cf6" },
          { label: "Vittorie", value: totals.wins || 0, icon: Trophy, color: "#22c55e" },
          {
            label: "Differenza reti",
            value: `${goalDiff >= 0 ? "+" : ""}${goalDiff}`,
            icon: Goal,
            color: "#f59e0b",
          },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="p-5 border-slate-200">
            <div className="flex items-center gap-3">
              <div
                className="h-9 w-9 rounded-xl flex items-center justify-center"
                style={{ background: `${color}15`, color }}
              >
                <Icon size={16} />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl leading-none">{value}</div>
                <div className="label-eyebrow mt-1">{label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {totals.matches_played > 0 && (
        <Card className="p-5 border-slate-200">
          <div className="flex items-center justify-around flex-wrap gap-4">
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-emerald-600 tabular-nums">
                {totals.wins || 0}
              </div>
              <div className="label-eyebrow mt-1">Vittorie</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-amber-500 tabular-nums">
                {totals.draws || 0}
              </div>
              <div className="label-eyebrow mt-1">Pareggi</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-red-500 tabular-nums">
                {totals.losses || 0}
              </div>
              <div className="label-eyebrow mt-1">Sconfitte</div>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div className="text-center">
              <div className="font-heading font-bold text-3xl tabular-nums">
                {totals.goals_scored || 0}
              </div>
              <div className="label-eyebrow mt-1">Goal fatti</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl tabular-nums">
                {totals.goals_conceded || 0}
              </div>
              <div className="label-eyebrow mt-1">Goal subiti</div>
            </div>
          </div>
        </Card>
      )}

      <Tabs defaultValue="presenze" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="presenze" data-testid="tab-presenze">
            Presenze
          </TabsTrigger>
          <TabsTrigger value="marcatori" data-testid="tab-scorers">
            Marcatori
          </TabsTrigger>
          <TabsTrigger value="assistman" data-testid="tab-assistmen">
            Assistman
          </TabsTrigger>
        </TabsList>

        <TabsContent value="presenze" className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <h2 className="font-heading text-xl font-semibold">Classifica presenze</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Giocatore</th>
                    <th className="px-3 py-3 font-semibold text-center">Conv.</th>
                    <th className="px-3 py-3 font-semibold">Allenamenti</th>
                    <th className="px-3 py-3 font-semibold">Partite</th>
                    <th className="px-3 py-3 font-semibold text-center">G</th>
                    <th className="px-3 py-3 font-semibold text-center">A</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr
                      key={p.player_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`stat-row-${p.player_id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="h-7 w-7 rounded-md bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">
                            {p.jersey_number ?? "—"}
                          </span>
                          <div>
                            <div className="font-medium">
                              {p.last_name} {p.first_name}
                            </div>
                            <div className="text-xs text-slate-500">{p.role || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-center">
                        <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold">
                          {p.convocations}
                        </span>
                      </td>
                      <td className="px-3 py-4 min-w-32">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold tabular-nums w-12">{p.training_rate}%</span>
                          <ProgressBar value={p.training_rate} color="#8b5cf6" />
                        </div>
                      </td>
                      <td className="px-3 py-4 min-w-32">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold tabular-nums w-12">{p.match_rate}%</span>
                          <ProgressBar value={p.match_rate} color="#22c55e" />
                        </div>
                      </td>
                      <td className="px-3 py-4 text-center font-semibold tabular-nums">
                        {p.goals || 0}
                      </td>
                      <td className="px-3 py-4 text-center font-semibold tabular-nums">
                        {p.assists || 0}
                      </td>
                    </tr>
                  ))}
                  {players.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-slate-400">
                        Aggiungi giocatori e registra le presenze per vedere le statistiche.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="marcatori" className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center gap-2">
              <Goal size={20} className="text-emerald-600" />
              <h2 className="font-heading text-xl font-semibold">Top marcatori</h2>
            </div>
            <ScorerTable rows={scorers.scorers} metric="goals" />
          </Card>
        </TabsContent>

        <TabsContent value="assistman" className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-5 border-slate-200 border-b flex items-center gap-2">
              <Footprints size={20} className="text-sky-600" />
              <h2 className="font-heading text-xl font-semibold">Top assistman</h2>
            </div>
            <ScorerTable rows={scorers.assistmen} metric="assists" />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
